import { graphql, useStaticQuery } from "gatsby";
import { useState, useRef, useEffect, useContext, useMemo, memo, useCallback } from "react";
import SwiperCore, { Navigation, Pagination, A11y } from "swiper";

import GIFT_PACKAGES_DATA from "../data/gift_packages.json";

import { gaCustomEvent } from "@helpers/browser";
import { setGorgiasChatVisibility } from "@helpers/gorgias";
import { getEligibleCartMetrics } from "@helpers/cart";
import { edgesToArray, parseText } from "@helpers/general";
import StoreContext from "@context/store";
import { CartContext } from "@context/CartProvider";
import { GeoContext } from "@context/GeoProvider";
import { LuxeContext } from "@context/LuxeProvider";
import { useALError, withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { HiddenElementsContext } from "@context/HiddenElements";
import { useTracking } from "@context/Tracking";

import { useShippingInfo } from "@hooks/useShippingInfo";
import { usePrevious } from "@hooks/usePrevious";
import useSalesColors from "@hooks/useSalesColors";
import useDiscountsInfo from "@hooks/useDiscountsInfo";
import useAnnouncementBar from "@hooks/useAnnouncementBar";
import useResponsiveWithHydrationFix from "@hooks/useResponsiveWithHydrationFix";

import { ALButtonV2, ALLoading, ALLink } from "@components/ALComponents";
import { applyCartDiscounts } from "@components/cartDiscounts";
import { ComponentType } from "@constants/ComponentType";
import TAG from "@constants/Tag";
import DISCOUNT_TYPES from "@constants/DiscountTypes";

import CheckoutButton from "@components/sideCart/CheckoutButton";
import TieredOfferProgressBarGift from "@components/al_components/TieredOfferProgressBarGift";
import TieredOfferProgressBar from "@components/al_components/TieredOfferProgressBar";
import GiftOfferProgressBar from "@components/al_components/GiftOfferProgressBar";

import CheckoutCallout from "@components/CheckoutCallout";
import SideCartHeader from "@components/sideCart/SideCartHeader";

import LuxeSelection from "@components/sideCart/LuxeSelection";
import SideCartItems from "@components/sideCart/SideCartItems";
import UpSellsRecommendation from "@components/sideCart/UpSellsRecommendation";
import PriceSummary from "@components/sideCart/PriceSummary";
import FreeGiftSelection from "@components/sideCart/FreeGiftSelection";
import LuxeGiftSideCart from "@components/sideCart/LuxeGiftSideCart";
import GiftPackaging from "@components/sideCart/GiftPackaging";
import GiftPackagingPopup from "@components/sideCart/GiftPackagingPopup";
import { LUXE_GIFT_LINE_ITEM_ATTRIBUTE_KEY, getLuxeContent } from "@helpers/luxe";
import "@components/sideCart/styles.scss";

SwiperCore.use([Navigation, Pagination, A11y]);


function SideCart() {
  const salesColors = useSalesColors();
  const context = useContext(StoreContext);
  const discountsInfo = useDiscountsInfo();
  const giftInfo = discountsInfo.find((s) => s.type === DISCOUNT_TYPES.GIFT_WITH_PURCHASE);
  const hasGiftDiscount = Boolean(giftInfo);
  const {
    luxe: { isLuxeActive, hasLuxeInCart, shouldAddLuxeInNextATC, isForcedGuest, subscriptionPlan },
    setLuxeActive,
  } = useContext(LuxeContext);
  const { cart, isLoadingCart, itemsInCart, toggleCart, isSideCartOpen } = useContext(CartContext);
  const hasBeenOpenOnce = useRef(false);
  const { trackCartProduct } = context.store;

  const { sendReport } = useALError();
  const { trackOpenSideCart, trackSwapFreeGift } = useTracking();
  const { gePriceDetails } = useContext(GeoContext);
  const shippingInfo = useShippingInfo();
  const { show, hide } = useContext(HiddenElementsContext);
  const prevSideCartOpen = usePrevious(isSideCartOpen);

  const [_finalAmount, setFinalAmout] = useState(0);
  const [shouldSendEvent, setShouldSendEvent] = useState(false);
  const [isNavigatingToCheckout, setIsNavigatingToCheckout] = useState(false);
  const [showFreeGiftSelection, setShowFreeGiftSelection] = useState(false);
  const [showGiftPackagingSelection, setShowGiftPackagingSelection] = useState(false);

  const sideCartHeaderRef = useRef(null);
  const sideCartCheckoutRef = useRef(null);

  const countryCode = gePriceDetails.CountryCode;

  if (!hasBeenOpenOnce.current && isSideCartOpen) {
    hasBeenOpenOnce.current = true;
  }

  const sideCartInfo = applyCartDiscounts(cart.lines, discountsInfo);
  const { lines } = sideCartInfo;

  useEffect(() => {
    try {
      if (isSideCartOpen) {
        hide();
        document.getElementsByTagName("body")[0].style.overflow = "hidden";
      } else {
        show();
        document.getElementsByTagName("body")[0].style.overflow = null;
      }
      setGorgiasChatVisibility(!isSideCartOpen);
    } catch (error) {
      console.log("Error sidecart set style");
      console.log(error);
    }
  }, [isSideCartOpen]);

  useEffect(() => {
    if (sideCartInfo.finalAmount !== 0 && sideCartInfo.finalAmount !== _finalAmount) {
      setFinalAmout(sideCartInfo.finalAmount);
    }
    if (!prevSideCartOpen && isSideCartOpen) {
      setShouldSendEvent(true);
    }
  }, [sideCartInfo, isLoadingCart]);

  useEffect(() => {
    try {
      if (shouldSendEvent && !isLoadingCart) {
        trackOpenSideCart(sideCartInfo, gePriceDetails.CurrencyCode);
        setShouldSendEvent(false);
      }
    } catch (error) {
      sendReport(error, { name: "SideCart", priority: "P5" });
    }
  }, [isLoadingCart, shouldSendEvent]);

  const isCheckoutDisabled =
    lines.length === 1 &&
    lines[0].variant.product.tags.includes(TAG.MYSTERY) &&
    lines[0].quantity === 1;

  const isLuxe = isLuxeActive || hasLuxeInCart || shouldAddLuxeInNextATC;

  const amountPercentInfo = discountsInfo?.find(
    ({ type }) => type === DISCOUNT_TYPES.AMOUNT_PERCENT
  );
  const quantityPercentInfo = discountsInfo?.find(
    ({ type }) => type === DISCOUNT_TYPES.QUANTITY_PERCENT
  );
  const tieredPercentInfo = amountPercentInfo || quantityPercentInfo;
  const isQuantityTieredDiscount = tieredPercentInfo?.type === DISCOUNT_TYPES.QUANTITY_PERCENT;

  const tierDiscounts = useMemo(() => {
    if (!tieredPercentInfo) return [];

    const thresholds = isQuantityTieredDiscount
      ? tieredPercentInfo?.thresholdConditionQuantity
      : tieredPercentInfo?.thresholdConditionAmount;

    return (
      thresholds?.map((threshold, index) => [
        parseFloat(threshold),
        parseFloat(tieredPercentInfo.thresholdResultPercentage[index]),
      ]) || []
    );
  }, [tieredPercentInfo, isQuantityTieredDiscount]);

  const percentOffInfo = discountsInfo?.find(
    ({ type }) => type === DISCOUNT_TYPES.SITEWIDE_PERCENT
  );

  const hasFreeGiftInCart = useMemo(
    () =>
      lines.some((line) =>
        line.attributes?.some(({ key, value }) => key === "_free_gift" && value === "true")
      ),
    [lines]
  );

  const { currentEligibleValue } = getEligibleCartMetrics({
    lines: cart.lines,
    discountsToCheck: tieredPercentInfo ? [tieredPercentInfo] : discountsInfo,
    excludeLuxeItems: true,
  });

  /**
   * Checkout callout conditions:
   *  1. AMOUNT_PERCENT discount OR PERCENT_OFF => currentEligibleValue > checkoutCalloutMinValue
   *  2. GIFT_WITH_PURCHASE and no AMOUNT_PERCENT => when free gift was added to cart
   *
   *  AMOUNT_PERCENT discount takes priority because of the tiered progress bar
   */
  const minValueCheckoutCallout = parseFloat(
    (tieredPercentInfo || percentOffInfo)?.checkoutCalloutMinValue
  );

  const showCheckoutCallout =
    hasGiftDiscount && !tieredPercentInfo
      ? hasFreeGiftInCart
      : currentEligibleValue >= minValueCheckoutCallout;

  const showTieredProgressTop = tieredPercentInfo && currentEligibleValue >= tierDiscounts[0]?.[0];

  const goToCheckout = async (url) => {
    setIsNavigatingToCheckout(true);

    const hasGiftCardInCart = cart.lines.some(
      (line) => line.variant.product.handle === "gift-card"
    );

    if (cart.lines.length === 2 && hasGiftCardInCart && hasLuxeInCart) {
      await setLuxeActive(false, subscriptionPlan, sideCartInfo);
    }

    trackCartProduct.forEach((productTracked) => {
      if (productTracked.origin.startsWith("recommended-")) {
        const readableFormatOrigin = productTracked.origin
          .replace("recommended-", "")
          .split("-")
          .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
          .join(" ");
        gaCustomEvent(
          `Recommended Product - ${readableFormatOrigin}`,
          "go-to-checkout",
          productTracked.productTitle
        );
      }
    });

    window.location.href = url;

    // Set the loading state to false after a few seconds, which is the average time to redirect to checkout
    // This prevents the loader from being stuck when the user swipes back from checkout on mobile browsers
    setTimeout(() => {
      setIsNavigatingToCheckout(false);
    }, 7000);
  };

  const textColorBanner = salesColors && salesColors?.sidecartBannerTextColor;

  const sideCartTags = useStaticQuery(graphql`
    query {
      allContentfulSideCartTag(filter: { node_locale: { eq: "en-US" }, titleTag: { ne: "null" } }) {
        edges {
          node {
            titleTag
            textColorTag {
              mobile
              desktop
            }
            backgroundColorTag {
              mobile
              desktop
            }
          }
        }
      }
    }
  `);
  const sideCartTag = edgesToArray(sideCartTags.allContentfulSideCartTag) || null;

  const {
    backgroundColor: responsiveBackgroundColorAB,
    mobileContent,
    desktopContent,
  } = useAnnouncementBar();

  const backgroundColorAB = useResponsiveWithHydrationFix(
    responsiveBackgroundColorAB.mobile,
    responsiveBackgroundColorAB.desktop
  );

  const abContent = useResponsiveWithHydrationFix(
    getLuxeContent(mobileContent.find((c) => c.textPosition === "Center")?.text || [], isLuxe)?.[0]
      ?.text || null,
    getLuxeContent(desktopContent.find((c) => c.textPosition === "Center")?.text || [], isLuxe)?.[0]
      ?.text || null
  );

  const handleSwapFreeGiftClick = useCallback(() => {
    setShowFreeGiftSelection(true);
    trackSwapFreeGift();
  }, [trackSwapFreeGift]);

  const showSidecartContent = itemsInCart > 0;

  const showTieredDiscountBanner =
    showSidecartContent && !hasGiftDiscount && showTieredProgressTop && !showCheckoutCallout;
  const showGiftAndTieredBanner =
    showSidecartContent && hasGiftDiscount && showTieredProgressTop && !showCheckoutCallout;
  const showGiftOnlyBanner =
    showSidecartContent &&
    hasGiftDiscount &&
    !showTieredProgressTop &&
    !showCheckoutCallout &&
    currentEligibleValue >= giftInfo?.freeShippingThreshold;

  const showABContent = tierDiscounts.length === 0 && !showCheckoutCallout && !showGiftOnlyBanner;

  const showABBanner = showABContent && !showCheckoutCallout;
  const showCheckoutCalloutBanner = !showABContent && showCheckoutCallout;

  const sharedStyle = {
    backgroundColor: backgroundColorAB,
    color: textColorBanner,
  };

  const baseClasses = "p-[10px] rounded-[4px] desktop:px-[15px] desktop:py-[12px]";

  const luxeGiftInCart = useMemo(
    () =>
      lines.find((line) =>
        line.attributes?.some(
          ({ key, value }) => key === LUXE_GIFT_LINE_ITEM_ATTRIBUTE_KEY && value === "true"
        )
      ),
    [lines]
  );

  return (
    <div className="al_sidecart">
      <div
        onClick={toggleCart}
        className={`sidecart_container ${
          isSideCartOpen ? "sidecart_container--isOpen" : "sidecart_container--isClose"
        }`}
      />
      <div
        className={`overflow-auto sidecart ${
          isSideCartOpen ? "sidecart--isOpen" : "sidecart--isClose"
        }`}
      >
        {isSideCartOpen && (isLoadingCart || isNavigatingToCheckout) && (
          <div className="sidecart_loader">
            <ALLoading />
          </div>
        )}

        <SideCartHeader
          sideCartHeaderRef={sideCartHeaderRef}
          sideCartTag={sideCartTag}
          toggleCart={toggleCart}
        />

        {showTieredDiscountBanner && (
          <div className={`mx-[15px] ${baseClasses}`} style={sharedStyle}>
            {currentEligibleValue >= tierDiscounts?.[0]?.[0] && (
              <TieredOfferProgressBar
                componentType={ComponentType.SIDE_CART}
                saleColors={salesColors}
                eligibleAmount={currentEligibleValue}
              />
            )}
          </div>
        )}

        {showGiftAndTieredBanner && (
          <div className={`mx-[15px] desktop:mx-[20px] ${baseClasses}`} style={sharedStyle}>
            <TieredOfferProgressBarGift
              componentType={ComponentType.SIDE_CART}
              saleColors={salesColors}
              eligibleAmount={currentEligibleValue}
            />
          </div>
        )}

        {showGiftOnlyBanner && (
          <div className={`mx-[15px] desktop:mx-[20px] ${baseClasses}`} style={sharedStyle}>
            <GiftOfferProgressBar
              saleColors={salesColors}
              eligibleAmount={currentEligibleValue}
              componentType={ComponentType.SIDE_CART}
            />
          </div>
        )}

        {showABBanner && (
          <div
            className="mx-[20px] mobile:mx-[15px] typo-micro font-bold text-center py-[12px] mobile:py-[10px] rounded-[4px]"
            style={sharedStyle}
          >
            {parseText(abContent)}
          </div>
        )}

        {showCheckoutCalloutBanner && (
          <div
            className="mx-[15px] desktop:mx-[20px] p-[10px] desktop:px-[15px] desktop:py-[12px] rounded-[4px]"
            style={sharedStyle}
          >
            <CheckoutCallout
              componentType={ComponentType.SIDE_CART}
              saleColors={salesColors}
              backgroundColorAB={backgroundColorAB}
            />
          </div>
        )}

        {itemsInCart === 0 && !isLoadingCart ? (
          <div className="px-[20px] mobile:px-[15px]">
            <p className="typo-h3 font-bold my-[25px] mobile:my-[20px] text-center">
              Your cart is empty.
            </p>
            <ALLink to="/collections/bestsellers" className="block mb-[15px] mobile:mb-[10px]">
              <ALButtonV2 size="large" variant="outline" onClick={toggleCart} fullWidth>
                Shop Best Sellers
              </ALButtonV2>
            </ALLink>
            <ALLink to="/collections/new">
              <ALButtonV2 size="large" variant="outline" onClick={toggleCart} fullWidth>
                Shop New Arrivals
              </ALButtonV2>
            </ALLink>
          </div>
        ) : (
          <>
            <div
              className="sidecart_content no-scrollbar"
              style={{
                paddingBottom: showTieredProgressTop
                  ? 0
                  : `${sideCartCheckoutRef?.current?.clientHeight}px`,
              }}
            >
              {showFreeGiftSelection && (
                <FreeGiftSelection
                  lines={cart.lines}
                  closeGiftSelection={() => setShowFreeGiftSelection(false)}
                />
              )}

              <GiftPackaging
                openPackagingPopup={() => setShowGiftPackagingSelection(true)}
                lines={lines}
              />
              {showGiftPackagingSelection && (
                <GiftPackagingPopup
                  onClose={() => setShowGiftPackagingSelection(false)}
                  lines={lines}
                  cartNote={cart?.note}
                  checkoutUrl={cart.checkoutUrl}
                  giftPackagesData={GIFT_PACKAGES_DATA}
                />
              )}

              <SideCartItems
                lineItems={lines}
                context={context}
                toggleCart={toggleCart}
                freegiftLineItemId={sideCartInfo.freegiftLineItemId}
                isLuxe={isLuxe}
                onSwapGift={handleSwapFreeGiftClick}
              />

              {!!luxeGiftInCart && <LuxeGiftSideCart giftLineItem={luxeGiftInCart} />}

              {hasBeenOpenOnce.current && <UpSellsRecommendation />}

              {!isForcedGuest && <LuxeSelection sideCartInfo={sideCartInfo} />}

              {!hasGiftDiscount && (
                <PriceSummary
                  sideCartInfo={sideCartInfo}
                  shippingInfo={shippingInfo}
                  isLuxe={isLuxe}
                  eligibleAmount={currentEligibleValue}
                />
              )}
              {showCheckoutCallout && (
                <div className="p-[20px] mobile:p-[15px] bg-light-grey">
                  <CheckoutButton
                    checkoutUrl={cart.checkoutUrl}
                    isCheckoutDisabled={isCheckoutDisabled}
                    countryCode={countryCode}
                    goToCheckout={goToCheckout}
                    isLuxe={isLuxe}
                    saleColors={salesColors}
                    componentType={ComponentType.SIDE_CART}
                    buttonText="Complete Checkout Now"
                  />
                </div>
              )}
            </div>

            <div className="static w-full bottom-0 z-[2] bg-light-grey">
              {hasGiftDiscount && (
                <PriceSummary
                  sideCartInfo={sideCartInfo}
                  shippingInfo={shippingInfo}
                  countryCode={countryCode}
                  isLuxe={isLuxe}
                  showCheckoutCallout={showCheckoutCallout}
                  eligibleAmount={currentEligibleValue}
                />
              )}
              {!showCheckoutCallout && (
                <div className="p-[20px] mobile:p-[15px]">
                  <CheckoutButton
                    checkoutUrl={cart.checkoutUrl}
                    isCheckoutDisabled={isCheckoutDisabled}
                    countryCode={countryCode}
                    goToCheckout={goToCheckout}
                    isLuxe={isLuxe}
                    saleColors={salesColors}
                    componentType={ComponentType.SIDE_CART}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default memo(
  withALErrorBoundary({
    name: "SideCart",
    priority: "P1",
  })(SideCart)
);
